import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { AxiosResponse } from "axios";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import {
  getErrorBodyMessage,
  getErrorResponseMessage,
} from "@via/utils/helpers.tsx";
import {
  ErrorNotification,
  Pagination,
  type PipelineInterface,
} from "@via/utils/interfaces.tsx";
import {
  CreateProcessRequestBody,
  DuplicatePipelineRequestBody,
  isPipelineUsedRequestBody,
  MenuGroupListItem,
  PaginatedResponse,
  PipelineBuilderNodeResponse,
  PipelineViewType,
  UpsertPipelineRequestBody,
  isProcessUsedResponsebody,
  PipelineRevision,
  ImportPipelineRequestBody,
  ExportPipelineRequestBody,
  ExportPipelineResponseBody,
  CheckImportViaPipelineResponseBody,
  ImportViaPipelineResponseBody,
  ImportViaPipelineRequestBody,
  ImportViaPipelineRequestQuery,
  MenuGroupCreateUpdateRequestBody,
  MenuGroupType,
  ExportViaPipelineRequestQuery,
  DownloadPipelineRequestQuery,
  PipelineListItem,
  LintReport,
} from "common";

export const fetchPipelineList = async (
  take: number,
  skip: number,
  pipelineType: PipelineViewType[],
  searchText?: string,
  userNameText?: string,
  includeAllVersions?: boolean
): Promise<Pagination<PipelineInterface>> => {
  try {
    const baseUrl = "/api/pipeline/v1";
    const queryParams = new URLSearchParams({
      take: `${take}`,
      skip: `${skip}`,
    });

    queryParams.append("type", pipelineType.join(","));

    if (searchText) {
      queryParams.append("searchKeyword", searchText);
    }
    if (userNameText) {
      queryParams.append("userNameKeyword", userNameText);
    }
    queryParams.append("includeAllVersions", `${includeAllVersions}`);

    const url = `${baseUrl}?${queryParams.toString()}`;
    const res = await axios.get(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const usePipelineList = (
  page: number = 0,
  pageSize: number = 10,
  pipelineType: PipelineViewType[] = [PipelineViewType.Released],
  searchText: string = "",
  userNameText: string = "",
  includeAllVersions: boolean = false
): any => {
  const skip = page * pageSize;
  const take = pageSize;
  return useQuery({
    queryKey: [
      "pipelineList",
      `/api/pipeline/v1`,
      take,
      skip,
      pipelineType,
      searchText,
      userNameText,
      includeAllVersions,
    ],
    queryFn: async () =>
      await fetchPipelineList(
        take,
        skip,
        pipelineType,
        searchText,
        userNameText,
        includeAllVersions
      ),
    retry: false,
  });
};

export const fetchPipelineListByProject = async (
  id: string,
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }>,
  tagIds: string[] = []
): Promise<PaginatedResponse<PipelineListItem>> => {
  try {
    const baseUrl = `/api/project/v1/${id}/pipelines`;
    const queryParams = new URLSearchParams({
      take: `${take}`,
      skip: `${skip}`,
    });

    if (sort.length > 0) {
      queryParams.append("sort", sort[0].id);
      queryParams.append("order", sort[0].desc ? "desc" : "asc");
    }
    tagIds.forEach(tagId => queryParams.append("tagIds", tagId));
    const url = `${baseUrl}?${queryParams.toString()}`;
    const res = await axios.get(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const usePipelineListByProject = (
  id: string,
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }>,
  tagIds: string[] = [],
  filter?: string
): UseQueryResult<PaginatedResponse<PipelineListItem>, Error> => {
  const skip = page * pageSize;
  const take = pageSize;
  return useQuery({
    queryKey: [
      "pipelineList",
      `/api/project/v1/${id}/pipelines`,
      take,
      skip,
      sort,
      tagIds,
      filter,
    ],
    queryFn: async () =>
      await fetchPipelineListByProject(id, take, skip, sort, tagIds),
    retry: true,
    retryOnMount: true,
  });
};

export const fetchProcessDetails = async (id: string): Promise<any> => {
  try {
    const processDetail = await axios.get(`/api/v1/process/${id}`);
    return processDetail.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const fetchPipelineDetails = async (
  id: string
): Promise<{
  pipeline: PipelineInterface;
  update: Record<string, unknown>;
}> => {
  try {
    const res = await axios.get(`/api/pipeline/v1/${id}/details`);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const usePipelineDetails = (id: string): any => {
  return useQuery({
    queryKey: ["pipelineData", `/api/pipeline/v1/${id}/details`],
    queryFn: async () => await fetchPipelineDetails(id),
    retry: false,
    refetchOnMount: "always",
    enabled: !!id,
    gcTime: 0,
  });
};

export const fetchPipelineLint = async (
  id: number | string
): Promise<LintReport> => {
  try {
    const res = await axios.get(`/api/pipeline/v1/${id}/lint`);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const usePipelineLint = (
  id?: number | string
): UseQueryResult<LintReport, Error> =>
  useQuery<LintReport>({
    queryKey: ["pipelineLint", `/api/pipeline/v1/${id}/lint`],
    queryFn: async () => await fetchPipelineLint(id as number | string),
    retry: false,
    refetchOnMount: "always",
    enabled: !!id,
    gcTime: 0,
  });

export const fetchPipelineRevisionHistory = async (
  id: string
): Promise<PipelineRevision> => {
  try {
    const res = await axios.get(`/api/pipeline/v1/${id}/revisions`);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const usePipelineRevisionHistory = (id: string): any => {
  return useQuery({
    queryKey: ["revisions", `/api/pipeline/v1/${id}/revisions`],
    queryFn: async () => await fetchPipelineRevisionHistory(id),
    enabled: !!id,
    retry: false,
  });
};

export const fetchPipelineBuilderOptions =
  async (): Promise<PipelineBuilderNodeResponse> => {
    try {
      const res = await axios.get("/api/project/v1/pipeline-builder-node");
      return res.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

export const fetchProcessMenuGroupsList = async (
  type: string
): Promise<PaginatedResponse<MenuGroupListItem>> => {
  try {
    const res = await axios.get("/api/v1/menu-group/process", {
      params: { type },
    });
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export function useProcessMenuGroupsList(
  type: string | undefined,
  enabled: boolean = true
): any {
  return useQuery({
    queryKey: ["/api/v1/menu-group/process", type],
    queryFn: async () => await fetchProcessMenuGroupsList(type as string),
    retry: false,
    enabled: enabled && Boolean(type),
  });
}

export const fetchPipelineMenuGroupsList = async (): Promise<
  PaginatedResponse<MenuGroupListItem>
> => {
  try {
    const res = await axios.get("/api/v1/menu-group/pipeline");
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const usePipelineMenuGroupsList = (enabled: boolean = true): any => {
  return useQuery({
    queryKey: ["/api/v1/menu-group/pipeline"],
    queryFn: async () => await fetchPipelineMenuGroupsList(),
    retry: false,
    enabled,
  });
};

export const useSaveProcessRevision = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async ({
      processId,
      payload,
    }: {
      processId: number;
      payload: CreateProcessRequestBody;
    }) => {
      const res = await axios.post(`/api/v1/process/${processId}`, payload);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "create-process-revision-success",
        message: "Process Successfully Revised",
        details: "New Process revision is now available",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/project/v1/pipeline-builder-node"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["processRevisions"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "create-process-revision-failed",
        message: "Process Revision Failed",
        details:
          error?.response?.data?.message ??
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useSaveNewProcess = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async payload => {
      const res = await axios.post("/api/v1/process", payload);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "process-creation-success",
        message: "Process Successfully Created",
        details: "New Process is now available",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/project/v1/pipeline-builder-node"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "process-creation-failed",
        message: "Process Creation Failed",
        details:
          error?.response?.data?.message ??
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useCreateOrUpdatePipeline = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (payload: UpsertPipelineRequestBody) => {
      const res = await axios.post("/api/pipeline/v1", payload);
      return res.data;
    },
    onSuccess: (_, payload: UpsertPipelineRequestBody): void => {
      const type = payload?.id ? "Pipeline Update" : "Pipeline Creation";
      setNotification({
        id: "pipeline-operation-success",
        message: `${type} Was Successful`,
        details: `${payload?.name} is now ready for use`,
        variant: NOTIFICATION.SUCCESS,
      });
      // Refresh revisions list altogether
      queryClient.invalidateQueries({
        queryKey: ["revisions"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/menu-group/pipeline"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/project/v1/pipeline-builder-node"],
      });
      queryClient.invalidateQueries({
        queryKey: ["pipelineLint"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "pipeline-operation-failed",
        message: "Pipeline Operation Failed",
        details:
          error?.response?.data?.message ||
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useCreatePipelineRevision = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (payload: UpsertPipelineRequestBody) => {
      const res = await axios.post("/api/pipeline/v1/revise", payload);
      return res.data;
    },
    onSuccess: (_, payload: UpsertPipelineRequestBody): void => {
      setNotification({
        id: "pipeline-revision-success",
        message: "Pipeline Successfully Revised",
        details: `${payload?.name} is ready for use`,
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["revisions"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/project/v1/pipeline-builder-node"],
      });
      queryClient.invalidateQueries({
        queryKey: ["pipelineLint"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "pipeline-revision-failed",
        message: "Pipeline Revision Failed",
        details:
          error?.response?.data.message ??
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const usePipelineBuilderOptions = (): any => {
  return useQuery({
    queryKey: ["/api/project/v1/pipeline-builder-node"],
    queryFn: async () => await fetchPipelineBuilderOptions(),
    retry: false,
  });
};

export const fetchProcessRevisionList = async (processId: string) => {
  try {
    const res = await axios.get(`/api/v1/process/${processId}/revisions`);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useProcessRevisionList = (processId: string): any => {
  return useQuery({
    queryKey: ["processRevisions", `/api/v1/process/${processId}/revisions`],
    queryFn: async () => await fetchProcessRevisionList(processId),
    enabled: !!processId,
    retry: false,
  });
};

export const fetchProcessExample = async (processId: string) => {
  try {
    const res = await axios.get(`/api/v1/process/${processId}/example`);
    return res.data;
  } catch (error) {
    // A 404 is the ordinary answer for a revision that has never had an example captured, not a
    // failure. Returning null keeps the Example tab hidden without logging an error for the
    // majority of revisions, which have no example and never will.
    //
    // The status is read off the error's shape rather than through axios.isAxiosError: the
    // `axios` in scope here is the app's configured instance, not the module, and the type guard
    // lives on the module.
    if (
      404 === (error as { response?: { status?: number } })?.response?.status
    ) {
      return null;
    }
    console.error(error);
    throw error;
  }
};

export const useProcessExample = (processId: string): any => {
  return useQuery({
    queryKey: ["processExample", `/api/v1/process/${processId}/example`],
    queryFn: async () => await fetchProcessExample(processId),
    enabled: !!processId,
    retry: false,
  });
};

export const useCreateProcessMenuGroup = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (
      payload: MenuGroupCreateUpdateRequestBody & { type: MenuGroupType }
    ) => {
      const res = await axios.post(
        "/api/v1/menu-group/process",
        { name: payload?.name },
        { params: { type: payload?.type } }
      );
      return res.data;
    },
    onSuccess: (data): void => {
      setNotification({
        id: "menu-group-creation-success",
        message: "Menu Group Successfully Added",
        details: `${data?.name} is now ready for use`,
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the project list
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/menu-group/process"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "menu-group-creation-failed",
        message: "Menu Group Creation Failed",
        details:
          error?.response?.data.message ||
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useCreateParameter = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async payload => {
      const res = await axios.post("/api/parameter/v1", payload);
      return res.data;
    },
    onSuccess: (data): void => {
      setNotification({
        id: "parameter-creation-success",
        message: "Parameter Successfully Added",
        details: `${data?.name} is now ready for use`,
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the project list
      queryClient.invalidateQueries({
        queryKey: ["params", "/api/parameter/v1"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "parameter-creation-failed",
        message: "Parameter Creation Failed",
        details:
          error?.response?.data.message ||
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useEditParameter = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async ({
      id,
      ...rest
    }: {
      id: number;
      rest: { name: string; identifier: string; fileType: string };
    }) => {
      const res = await axios.post(`/api/parameter/v1/${id}`, rest);
      return res.data;
    },
    onSuccess: (data): void => {
      setNotification({
        id: "parameter-revision-success",
        message: "Parameter Successfully Revised",
        details: `${data?.name} is now ready for use`,
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the project list
      queryClient.invalidateQueries({
        queryKey: ["params", "/api/parameter/v1"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "parameter-revision-failed",
        message: "Parameter Revision Failed",
        details:
          error?.response?.data.message ||
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDeleteParameter = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await axios.delete(`/api/parameter/v1/${id}`);
      return res.data;
    },
    onSuccess: (data): void => {
      setNotification({
        id: "parameter-deletion-success",
        message: "Parameter Successfully Deleted",
        details: data?.message ?? "This action cannot be undone",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the project list
      queryClient.invalidateQueries({
        queryKey: ["params", "/api/parameter/v1"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "parameter-deletion-failed",
        message: "Parameter Deletion Failed",
        details:
          error?.response?.data.message ||
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      return error?.response?.data.message;
    },
  });
};

export const useCreatePipelineMenuGroup = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async payload => {
      const res = await axios.post("/api/v1/menu-group/pipeline", payload);
      return res.data;
    },
    onSuccess: (data): void => {
      setNotification({
        id: "menu-group-creation-success",
        message: "Menu Group Successfully Added",
        details: `${data?.name} is now ready for use`,
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the project list
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/menu-group/pipeline"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "menu-group-creation-failed",
        message: "Menu Group Creation Failed",
        details:
          error?.response?.data.message ||
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useEditPipelineMenuGroup = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async ({
      rename,
      value,
    }: {
      rename: string;
      value: number;
    }) => {
      const payload = {
        name: rename,
      };
      const res = await axios.post(
        `/api/v1/menu-group/pipeline/${value}`,
        payload
      );
      return res.data;
    },
    onSuccess: (data): void => {
      setNotification({
        id: "pipeline-menu-group-revision-success",
        message: "Pipeline Menu Group Successfully Revised",
        details: `${data?.name} is now ready for use`,
        variant: NOTIFICATION.SUCCESS,
      });

      queryClient.invalidateQueries({
        queryKey: ["/api/v1/menu-group/pipeline"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "pipeline-menu-group-revision-failed",
        message: "Pipeline Menu Group Revision Failed",
        details:
          error?.response?.data.message ||
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDeletePipelineMenuGroup = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async ({ menuGroup }: { menuGroup: number }) => {
      const res = await axios.delete(
        `/api/v1/menu-group/pipeline/${menuGroup}`
      );
      return res.data;
    },
    onSuccess: (data): void => {
      setNotification({
        id: "pipeline-menu-group-deletion-success",
        message: "Pipeline Menu Group Successfully Deleted",
        details: data?.message ?? "This action cannot be undone",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the project list
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/menu-group/pipeline"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "pipeline-menu-group-deletion-failed",
        message: "Pipeline Menu Group Deletion Failed",
        details:
          error?.response?.data.message ||
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      return error?.response?.data.message;
    },
  });
};

export const useEditMenuGroup = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async ({
      rename,
      value,
      type,
    }: {
      rename: string;
      value: number;
      type: MenuGroupType;
    }) => {
      const payload = {
        name: rename,
      };
      const res = await axios.post(
        `/api/v1/menu-group/process/${value}`,
        payload,
        { params: { type } }
      );
      return res.data;
    },
    onSuccess: (data): void => {
      setNotification({
        id: "menu-group-revision-success",
        message: "Menu Group Successfully Revised",
        details: `${data?.name} is now ready for use`,
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the project list
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/menu-group/process"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "menu-group-revision-failed",
        message: "Menu Group Revision Failed",
        details:
          error?.response?.data.message ||
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDeleteMenuGroup = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async ({ menuGroup }: { menuGroup: number }) => {
      const res = await axios.delete(`/api/v1/menu-group/process/${menuGroup}`);
      return res.data;
    },
    onSuccess: (data): void => {
      setNotification({
        id: "menu-group-deletion-success",
        message: "Menu Group Successfully Deleted",
        details: data?.message ?? "This action cannot be undone",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the project list
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/menu-group/process"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "menu-group-deletion-failed",
        message: "Menu Group Deletion Failed",
        details:
          error?.response?.data.message ||
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      return error?.response?.data.message;
    },
  });
};

export const useRemovePipelineFromProject = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async ({
      projectId,
      pipelineId,
    }: {
      projectId: string;
      pipelineId: number;
    }) => {
      const res = await axios.delete(
        `/api/project/v1/${projectId}/pipeline/${pipelineId}`
      );
      return res.data;
    },
    onSuccess: (data): void => {
      setNotification({
        id: "remove-pipeline-from-project-success",
        message: "Pipeline Successfully Removed from Project",
        details: data?.message ?? "This action cannot be undone",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the project list
      queryClient.invalidateQueries({
        queryKey: ["pipelineList"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "remove-pipeline-from-project-failed",
        message: "Pipeline Removal Failed",
        details:
          error?.response?.data.message ||
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      return error?.response?.data.message;
    },
  });
};

export const useOverwriteRevision = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async ({
      processId,
      payload,
    }: {
      processId: number;
      payload: CreateProcessRequestBody;
    }) => {
      const res = await axios.put(`/api/v1/process/${processId}`, payload);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "process-overwrite-success",
        message: "Process Successfully Overwritten",
        details: "Process revision is now available",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/project/v1/pipeline-builder-node"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "process-overwrite-failed",
        message: "Process Overwrite Failed",
        details:
          error?.response?.data?.message ??
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};
export const useDuplicatePipeline = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (payload: DuplicatePipelineRequestBody) => {
      const res = await axios.post("/api/pipeline/v1/duplicate", payload);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "pipeline-duplicate-success",
        message: `Pipeline Duplicate Was Successful`,
        details: `Now ready for use`,
        variant: NOTIFICATION.SUCCESS,
      });
      // Refresh revisions list altogether
      queryClient.invalidateQueries({
        queryKey: ["revisions"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/menu-group/pipeline"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/project/v1/pipeline-builder-node"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "pipeline-duplicate-failed",
        message: "Pipeline Duplicate Failed",
        details: "We were unable to duplicate your changes",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDuplicateProcess = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (processId: number) => {
      const res = await axios.post(`/api/v1/process/${processId}/duplicate`);
      return { ...res.data };
    },
    onSuccess: (): void => {
      setNotification({
        id: "duplicate-process-success",
        message: "Process Successfully Duplicated",
        details: "Duplicated Process is now available",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/project/v1/pipeline-builder-node"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["processRevisions"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "duplicate-process-failed",
        message: "Process Duplication Failed",
        details:
          error?.response?.data?.message ??
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDeletePipeline = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (pipelineId: number) => {
      const res = await axios.delete(`/api/pipeline/v1/${pipelineId}`);
      return res.data;
    },
    onSuccess: (data): void => {
      setNotification({
        id: "pipeline-deletion-success",
        message: "Pipeline Successfully Deleted",
        details: data?.message ?? "This action cannot be undone",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the project list
      queryClient.invalidateQueries({
        queryKey: ["pipelineList"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "pipeline-deletion-failed",
        message: "Pipeline Deletion Failed",
        details:
          error?.response?.data.message ||
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      return error?.response?.data.message;
    },
  });
};

export const usePipelineIsUsed = (): any => {
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async ({ payload }: { payload: isPipelineUsedRequestBody }) => {
      const res = await axios.post(`/api/pipeline/v1/is-used`, payload);
      return res.data;
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "pipeline-is-used-failed",
        message: "Pipeline Is Used Check Failed",
        details:
          error?.response?.data.message ||
          "Something went wrong while checking pipeline usage",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const fetchProcessIsUsed = async (
  processId: string
): Promise<isProcessUsedResponsebody> => {
  try {
    const res = await axios.get(`/api/v1/process/${processId}/is-used`);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useProcessIsUsed = (processId: string): any => {
  return useQuery({
    queryKey: [`/api/v1/process/${processId}/is-used`],
    queryFn: async () => await fetchProcessIsUsed(processId),
    enabled: !!processId,
    retry: false,
  });
};

export const useUploadViaPipeline = (): UseMutationResult<
  CheckImportViaPipelineResponseBody,
  ErrorNotification,
  File
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    CheckImportViaPipelineResponseBody,
    ErrorNotification,
    File
  >({
    mutationFn: async (
      file: File
    ): Promise<CheckImportViaPipelineResponseBody> => {
      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(
        "/api/pipeline/v1/import/via/check",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "via-pipeline-file-upload-successfully",
        message: "Pipeline File Uploaded Successfully",
        details: "Pipeline File is successfully uploaded",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "via-pipeline-file-upload-failed",
        message: "Uploading Pipeline File Failed",
        details:
          getErrorResponseMessage(error) ??
          "We were unable to upload your file",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useImportPipeline = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (payload: ImportPipelineRequestBody) => {
      const res = await axios.post("/api/pipeline/v1/import", payload);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "pipeline-import-success",
        message: "Pipeline Import Was Successful",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate pipeline details to ensure fresh data is fetched after import
      queryClient.invalidateQueries({
        queryKey: ["pipelineData"],
        refetchType: "all",
      });
      // Refresh revisions list altogether
      queryClient.invalidateQueries({
        queryKey: ["revisions"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/project/v1/pipeline-builder-node"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "pipeline-import-failed",
        message: "Pipeline Import Failed",
        details:
          error?.response?.data?.message ??
          "We were unable to import your pipeline",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useExportPipeline = (): UseMutationResult<
  ExportPipelineResponseBody,
  ErrorNotification,
  ExportPipelineRequestBody
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<
    ExportPipelineResponseBody,
    ErrorNotification,
    ExportPipelineRequestBody
  >({
    mutationFn: async (
      payload: ExportPipelineRequestBody
    ): Promise<ExportPipelineResponseBody> => {
      const res = await axios.post("/api/pipeline/v1/export", payload);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "pipeline-export-success",
        message: "Pipeline Export Was Successful",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "pipeline-export-failed",
        message: "Pipeline Export Failed",
        details:
          error?.response?.data?.message ??
          "We were unable to export your pipeline",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useImportViaPipeline = (): UseMutationResult<
  ImportViaPipelineResponseBody,
  ErrorNotification,
  ImportViaPipelineRequestBody & ImportViaPipelineRequestQuery
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    ImportViaPipelineResponseBody,
    ErrorNotification,
    ImportViaPipelineRequestBody & ImportViaPipelineRequestQuery
  >({
    mutationFn: async (
      payload: ImportViaPipelineRequestBody & ImportViaPipelineRequestQuery
    ): Promise<ImportViaPipelineResponseBody> => {
      const res = await axios.post(
        "/api/pipeline/v1/import/via",
        {
          menuGroups: payload.menuGroups,
          // The user's answers to any revision identity conflicts, keyed by
          // process_rev_uuid. Dropping them here used to make the whole choice a
          // no-op: the backend fails a reported conflict with no entry, so the
          // import came back as a 409 no matter what the user picked.
          conflictResolutions: payload.conflictResolutions,
        },
        { params: { fileId: payload.fileId } }
      );
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "import-via-pipeline-file-upload-successfully",
        message: "Import Via Pipeline Uploaded Successfully",
        details: "Import Via Pipeline is successfully uploaded",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "import-via-pipeline-file-upload-failed",
        message: "Uploading Via Pipeline  Failed",
        details:
          getErrorResponseMessage(error) ??
          "We were unable to upload your file",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDeleteProcessRevision = (): UseMutationResult<
  void,
  ErrorNotification,
  number
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<void, ErrorNotification, number>({
    mutationFn: async (processId: number): Promise<void> => {
      await axios.delete(`/api/v1/process/${processId}`);
    },
    onSuccess: (): void => {
      setNotification({
        id: "delete-process-revision-success",
        message: "Process Revision Successfully Deleted",
        details: "This action cannot be undone",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["processRevisions"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/project/v1/pipeline-builder-node"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "delete-process-revision-failed",
        message: "Process Revision Deletion Failed",
        details:
          error?.response?.data?.message ??
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDownloadViaPipeline = (): UseMutationResult<
  AxiosResponse<Blob>,
  ErrorNotification,
  ExportViaPipelineRequestQuery
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    AxiosResponse<Blob>,
    ErrorNotification,
    ExportViaPipelineRequestQuery
  >({
    mutationFn: ({ pipelineId }) =>
      axios.get(`/api/pipeline/v1/export/via?pipelineId=${pipelineId}`, {
        responseType: "blob",
        withCredentials: true,
      }),
    onSuccess: () => {
      setNotification({
        id: "foundry-export-success",
        message: "Foundry file exported",
        details: "Foundry .vf file is downloading.",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    // responseType "blob" makes the error body a Blob as well, so the refusal
    // text is only reachable by reading it.
    onError: async error => {
      setNotification({
        id: "foundry-export-failed",
        message: "Export failed",
        details:
          (await getErrorBodyMessage(error)) ??
          "We couldn't export the Foundry file.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useDownloadPipeline = (): UseMutationResult<
  AxiosResponse<Blob>,
  ErrorNotification,
  DownloadPipelineRequestQuery
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    AxiosResponse<Blob>,
    ErrorNotification,
    DownloadPipelineRequestQuery
  >({
    mutationFn: ({ pipelineId }) =>
      axios.get(`/api/pipeline/v1/download?pipelineId=${pipelineId}`, {
        responseType: "blob",
        withCredentials: true,
      }),
    onSuccess: () => {
      setNotification({
        id: "pipeline-download-success",
        message: "Pipeline downloaded",
        details: "",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    // responseType "blob" makes the error body a Blob as well, so the refusal
    // text is only reachable by reading it.
    onError: async error => {
      setNotification({
        id: "pipeline-download-failed",
        message: "Download failed",
        details:
          (await getErrorBodyMessage(error)) ??
          "If issue persists, please contact support.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};
